import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { setSelectedItem } from "@/features/bughurd/slice";
import { toast } from "react-toastify";
import {
  useGetWidgetByProjectIdQuery,
  useCreateFeedbackWidgetMutation,
  useUpdateFeedbackWidgetMutation,
} from "@/features/bughurd/api";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Switch,
  IconButton,
  Button,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  Grid,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";

import type { WidgetConfig, CustomQuestion } from "./types";
import { PRESET_COLORS, DEFAULT_CFG } from "./constants";
import QRow from "./QRow";
import WidgetPreview from "./WidgetPreview";

// ─── Reusable Section Components ────
const SectionCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({
  children,
  sx,
}) => <Box sx={{ mb: 0.5, ...sx }}>{children}</Box>;

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="overline"
    sx={{
      fontWeight: 700,
      color: "text.secondary",
      letterSpacing: 1,
      mb: 1,
      display: "block",
      fontSize: 10,
    }}
  >
    {children}
  </Typography>
);

// ─── Main Configurator ───────────────────────────────────────────────
const FeedbackWidgetConfigurator: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();
  const [cfg, setCfg] = useState<WidgetConfig>(DEFAULT_CFG);
  const [cTab, setCTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);

  const { data: widgetData } =
    useGetWidgetByProjectIdQuery({ projectId }, { skip: !projectId });
  const [createWidget, { isLoading: isCreating }] =
    useCreateFeedbackWidgetMutation();
  const [updateWidget, { isLoading: isUpdating }] =
    useUpdateFeedbackWidgetMutation();

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    if (widgetData?.data?.decryptedData) {
      const w = widgetData.data.decryptedData;
      if (w._id) setWidgetId(w._id);
      const apiCats = w.categories ?? {};
      setCfg({
        brandColor: w.brandColor ?? DEFAULT_CFG.brandColor,
        position: w.position ?? DEFAULT_CFG.position,
        buttonText: w.buttonText ?? DEFAULT_CFG.buttonText,
        companyName: w.companyName ?? DEFAULT_CFG.companyName,
        categories: {
          bugReport: apiCats.bugReport ?? DEFAULT_CFG.categories.bugReport,
          generalFeedback: apiCats.generalFeedback ?? DEFAULT_CFG.categories.generalFeedback,
          featureRequest: apiCats.featureRequest ?? DEFAULT_CFG.categories.featureRequest,
        },
        customQuestions: w.customQuestions ?? DEFAULT_CFG.customQuestions,
        poweredBy: w.poweredBy ?? DEFAULT_CFG.poweredBy,
        darkMode: w.darkMode ?? DEFAULT_CFG.darkMode,
        collectEmail: w.collectEmail ?? DEFAULT_CFG.collectEmail,
        maxRecordSec: w.maxRecordSec ?? DEFAULT_CFG.maxRecordSec,
      });
      setInitialized(true);
    }
  }, [widgetData, initialized]);

  const handleSave = async () => {
    const payload = { ...cfg, projectId };
    try {
      if (widgetId) {
        await updateWidget({ feedbackWidgetId: widgetId, ...payload }).unwrap();
        toast.success("Widget updated successfully");
      } else {
        const res = await createWidget(payload).unwrap();
        if (res?.data?._id) setWidgetId(res.data._id);
        toast.success("Widget created successfully");
      }
    } catch {
      toast.error("Failed to save widget configuration");
    }
  };

  const upd = (p: Partial<WidgetConfig>) => setCfg((v) => ({ ...v, ...p }));
  const addQ = () =>
    upd({
      customQuestions: [
        ...cfg.customQuestions,
        {
          id: "q" + Date.now(),
          type: "text",
          label: "New Question",
          options: [],
          required: false,
          category: "all",
          condition: null,
        },
      ],
    });
  const updQ = (i: number, q: CustomQuestion) => {
    const a = [...cfg.customQuestions];
    a[i] = q;
    upd({ customQuestions: a });
  };
  const delQ = (i: number) =>
    upd({ customQuestions: cfg.customQuestions.filter((_, j) => j !== i) });

  const handleDragStart = (e: React.DragEvent, i: number) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(i);
  };
  const handleDragEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      const a = [...cfg.customQuestions];
      const [item] = a.splice(dragIdx, 1);
      a.splice(dragOverIdx, 0, item);
      upd({ customQuestions: a });
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const conditionCount = cfg.customQuestions.filter((q) => q.condition).length;

  const embedCode = `<!-- Sniffer Feedback Widget -->\n<script>\n  (function(s,n,i,f){s.SnifferWidget=f;s[f]=s[f]||function(){\n  (s[f].q=s[f].q||[]).push(arguments)};var e=n.createElement(i);\n  e.async=1;e.src='https://cdn.sniffer.dev/widget.js';\n  n.getElementsByTagName(i)[0].parentNode.insertBefore(e,\n  n.getElementsByTagName(i)[0])})(window,document,'script','sniffer');\n\n  sniffer('init', {\n    projectId: 'proj_xxxxxxxx',\n    brandColor: '${cfg.brandColor}',\n    position: '${cfg.position}',\n    darkMode: ${cfg.darkMode}\n  });\n</script>`;


  return (
    <Box
      sx={{
        height: "calc(100vh - 100px)",
        minHeight: 500,
        display: "flex",
        overflow: "hidden",
        bgcolor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.06)}`,
        mx: 1,
        mt: 0.5,
        mb: 1,
      }}
    >
      {/* ─── LEFT CONFIG PANEL ─── */}
      <Box
        sx={{
          width: 460,
          borderRight: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          zIndex: 2,
          borderRadius: "11px 0 0 11px",
          overflow: "hidden",
        }}
      >
        {/* Header with back button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Tooltip title="Back to Feedback">
            <IconButton
              size="small"
              onClick={() => {
                dispatch(setSelectedItem("Feedback"));
                navigate(`/defect-tracker/projects/${projectId}/feedback`);
              }}
              sx={{
                bgcolor: theme.palette.action.hover,
                "&:hover": { bgcolor: theme.palette.action.selected },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ lineHeight: 1.3 }}
            >
              Widget Configurator
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              Customize your feedback widget
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={isCreating || isUpdating}
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleSave}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              px: 2,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {isCreating || isUpdating ? "Saving..." : widgetId ? "Update" : "Save"}
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs
          value={cTab}
          onChange={(_, v) => setCTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 44,
            "& .MuiTabs-indicator": {
              height: 2.5,
              borderRadius: "2px 2px 0 0",
            },
          }}
        >
          <Tab
            icon={<PaletteOutlinedIcon sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Appearance"
            sx={{
              minHeight: 44,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              gap: 0.5,
              letterSpacing: 0.2,
            }}
          />
          <Tab
            icon={<QuizOutlinedIcon sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Questions"
            sx={{
              minHeight: 44,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              gap: 0.5,
              letterSpacing: 0.2,
            }}
          />
          <Tab
            icon={<CodeOutlinedIcon sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Embed"
            sx={{
              minHeight: 44,
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              gap: 0.5,
              letterSpacing: 0.2,
            }}
          />
        </Tabs>

        {/* Scrollable content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2.5,
            py: 2,
            bgcolor: theme.palette.background.paper,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.primary, 0.1),
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          }}
        >
          {/* ═══ APPEARANCE TAB ═══ */}
          {cTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* General Settings */}
              <SectionCard>
                <SectionLabel>General Settings</SectionLabel>
                <Grid container spacing={1.5}>
                  <Grid item xs={7}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 0.5, display: "block", fontSize: 11 }}
                    >
                      Company Name
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={cfg.companyName}
                      onChange={(e) => upd({ companyName: e.target.value })}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 0.5, display: "block", fontSize: 11 }}
                    >
                      Max Recording (sec)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={cfg.maxRecordSec}
                      onChange={(e) =>
                        upd({ maxRecordSec: parseInt(e.target.value) || 120 })
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
              <Divider sx={{ my: 2 }} />
              {/* Brand Color */}
              <SectionCard>
                <SectionLabel>Brand Color</SectionLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    component="label"
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "6px",
                      bgcolor: cfg.brandColor,
                      flexShrink: 0,
                      boxShadow: `0 1px 4px ${alpha(cfg.brandColor, 0.3)}`,
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      border: `1.5px solid ${theme.palette.divider}`,
                      "&:hover": { opacity: 0.85 },
                    }}
                  >
                    <input
                      type="color"
                      value={cfg.brandColor}
                      onChange={(e) => upd({ brandColor: e.target.value })}
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        border: "none",
                      }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    value={cfg.brandColor}
                    onChange={(e) => upd({ brandColor: e.target.value })}
                    placeholder="#FF8C21"
                    sx={{
                      width: 90,
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: 11,
                        fontWeight: 600,
                        py: "5px",
                      },
                      "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                    }}
                  />
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                  {PRESET_COLORS.map((c) => (
                    <Tooltip key={c} title={c} placement="top" arrow>
                      <Box
                        onClick={() => upd({ brandColor: c })}
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "5px",
                          bgcolor: c,
                          cursor: "pointer",
                          flexShrink: 0,
                          border:
                            cfg.brandColor === c
                              ? `2px solid ${theme.palette.text.primary}`
                              : "2px solid transparent",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            transform: "scale(1.15)",
                            boxShadow: `0 2px 6px ${alpha(c, 0.4)}`,
                          },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {cfg.brandColor === c && (
                          <CheckIcon
                            sx={{
                              fontSize: 10,
                              color: "#fff",
                              filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.3))",
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              </SectionCard>
              <Divider sx={{ my: 2 }} />
              {/* Widget Position */}
              <SectionCard>
                <SectionLabel>Widget Position</SectionLabel>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[
                    {
                      id: "floating" as const,
                      label: "Floating",
                      desc: "Bottom-right",
                    },
                    {
                      id: "side" as const,
                      label: "Side Tab",
                      desc: "Right edge",
                    },
                  ].map((p) => {
                    const selected = cfg.position === p.id;
                    return (
                      <Box
                        key={p.id}
                        onClick={() => upd({ position: p.id })}
                        sx={{
                          flex: 1,
                          p: 1.25,
                          borderRadius: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          border: `1.5px solid ${selected ? cfg.brandColor : theme.palette.divider}`,
                          bgcolor: selected
                            ? alpha(cfg.brandColor, 0.06)
                            : theme.palette.background.default,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: selected
                              ? cfg.brandColor
                              : theme.palette.text.disabled,
                          },
                        }}
                      >
                        {/* Mini illustration */}
                        <Box
                          sx={{
                            width: 36,
                            height: 28,
                            borderRadius: "4px",
                            flexShrink: 0,
                            border: `1px solid ${alpha(selected ? cfg.brandColor : theme.palette.text.disabled, 0.3)}`,
                            position: "relative",
                            overflow: "hidden",
                            bgcolor: alpha(
                              selected
                                ? cfg.brandColor
                                : theme.palette.text.disabled,
                              0.04,
                            ),
                          }}
                        >
                          {p.id === "floating" ? (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 2,
                                right: 2,
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: selected
                                  ? cfg.brandColor
                                  : theme.palette.text.disabled,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: "absolute",
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: 4,
                                height: 14,
                                borderRadius: "2px 0 0 2px",
                                bgcolor: selected
                                  ? cfg.brandColor
                                  : theme.palette.text.disabled,
                              }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color: selected ? cfg.brandColor : "text.primary",
                              fontSize: 11,
                              lineHeight: 1.3,
                            }}
                          >
                            {p.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 9 }}
                          >
                            {p.desc}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </SectionCard>
              <Divider sx={{ my: 2 }} />
              {/* Categories */}
              <SectionCard>
                <SectionLabel>Feedback Categories</SectionLabel>
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  {[
                    {
                      id: "bugReport" as const,
                      label: "Bug Report",
                      icon: <BugReportOutlinedIcon sx={{ fontSize: 15 }} />,
                      color: "#FF4D6A",
                    },
                    {
                      id: "generalFeedback" as const,
                      label: "Feedback",
                      icon: <ChatOutlinedIcon sx={{ fontSize: 15 }} />,
                      color: "#3B82F6",
                    },
                    {
                      id: "featureRequest" as const,
                      label: "Feature",
                      icon: <LightbulbOutlinedIcon sx={{ fontSize: 15 }} />,
                      color: "#FBBF24",
                    },
                  ].map((cat) => {
                    const active = cfg.categories[cat.id];
                    return (
                      <Box
                        key={cat.id}
                        onClick={() => {
                          setCfg((prev) => ({
                            ...prev,
                            categories: {
                              ...prev.categories,
                              [cat.id]: !prev.categories[cat.id],
                            },
                          }));
                        }}
                        sx={{
                          flex: 1,
                          p: "8px 10px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          border: `1.5px solid ${active ? alpha(cat.color, 0.4) : theme.palette.divider}`,
                          bgcolor: active
                            ? alpha(cat.color, 0.05)
                            : "transparent",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            bgcolor: active
                              ? alpha(cat.color, 0.08)
                              : theme.palette.action.hover,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "6px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: active
                              ? alpha(cat.color, 0.15)
                              : theme.palette.action.hover,
                            color: active ? cat.color : "text.disabled",
                          }}
                        >
                          {cat.icon}
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: 11, lineHeight: 1.2 }}
                        >
                          {cat.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </SectionCard>
              <Divider sx={{ my: 2 }} />
              {/* Options Toggles */}
              <SectionCard>
                <SectionLabel>Options</SectionLabel>
                <Box
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                  }}
                >
                  {[
                    {
                      key: "darkMode" as const,
                      label: "Dark Mode",
                      desc: "Dark theme for widget",
                      icon: <DarkModeOutlinedIcon sx={{ fontSize: 16 }} />,
                      color: "#6366F1",
                    },
                    {
                      key: "collectEmail" as const,
                      label: "Collect Email",
                      desc: "Ask for user email",
                      icon: <EmailOutlinedIcon sx={{ fontSize: 16 }} />,
                      color: "#3B82F6",
                    },
                    {
                      key: "poweredBy" as const,
                      label: "Powered by Sniffer",
                      desc: "Show attribution",
                      icon: (
                        <VolunteerActivismOutlinedIcon sx={{ fontSize: 16 }} />
                      ),
                      color: "#14B8A6",
                    },
                  ].map((o, i, arr) => (
                    <Box
                      key={o.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderBottom:
                          i < arr.length - 1
                            ? `1px solid ${theme.palette.divider}`
                            : "none",
                        bgcolor: cfg[o.key]
                          ? alpha(theme.palette.primary.main, 0.03)
                          : "transparent",
                        transition: "background 0.15s ease",
                      }}
                    >
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "6px",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: cfg[o.key]
                            ? alpha(o.color, 0.12)
                            : theme.palette.action.hover,
                          color: cfg[o.key] ? o.color : "text.disabled",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {o.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: 12, lineHeight: 1.2 }}
                        >
                          {o.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: 10 }}
                        >
                          {o.desc}
                        </Typography>
                      </Box>
                      <Switch
                        checked={cfg[o.key]}
                        onChange={() => upd({ [o.key]: !cfg[o.key] })}
                        size="small"
                      />
                    </Box>
                  ))}
                </Box>
              </SectionCard>
            </Box>
          )}

          {/* ═══ QUESTIONS & LOGIC TAB ═══ */}
          {cTab === 1 && (
            <Box>
              <SectionCard sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ lineHeight: 1.3 }}
                    >
                      Questions & Logic
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cfg.customQuestions.length} questions · {conditionCount}{" "}
                      conditional
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addQ}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      fontSize: 11,
                      px: 1.5,
                    }}
                  >
                    Add
                  </Button>
                </Box>
              </SectionCard>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  mb: 2.5,
                }}
              >
                {cfg.customQuestions.map((q, i) => (
                  <QRow
                    key={q.id}
                    q={q}
                    idx={i}
                    allQs={cfg.customQuestions}
                    onUpdate={(u) => updQ(i, u)}
                    onDelete={() => delQ(i)}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    isDragOver={dragOverIdx === i && dragIdx !== i}
                  />
                ))}
                {cfg.customQuestions.length === 0 && (
                  <SectionCard sx={{ textAlign: "center", py: 5 }}>
                    <QuizOutlinedIcon
                      sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      No questions yet
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Click "Add" to create your first question
                    </Typography>
                  </SectionCard>
                )}
              </Box>
            </Box>
          )}

          {/* ═══ EMBED TAB ═══ */}
          {cTab === 2 && (
            <Box>
              <SectionCard sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                    }}
                  >
                    <CodeOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ lineHeight: 1.3 }}
                    >
                      Installation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add before{" "}
                      <code
                        style={{
                          background: theme.palette.action.hover,
                          padding: "1px 5px",
                          borderRadius: 3,
                          fontFamily: "monospace",
                          fontSize: 10,
                        }}
                      >
                        &lt;/body&gt;
                      </code>
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      p: "8px 14px",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.03)
                          : alpha(theme.palette.common.black, 0.02),
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#FF5F57",
                        }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#FEBC2E",
                        }}
                      />
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#28C840",
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="text.disabled"
                        sx={{ fontFamily: "monospace", ml: 0.5 }}
                      >
                        HTML
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      startIcon={
                        copied ? (
                          <CheckIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        )
                      }
                      onClick={() => {
                        navigator.clipboard.writeText(embedCode);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        fontWeight: 600,
                        color: copied ? "#00E5A0" : "text.secondary",
                        borderRadius: "6px",
                        minWidth: "auto",
                        px: 1,
                      }}
                    >
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      p: 2,
                      m: 0,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.black, 0.3)
                          : "#1A1F26",
                      color: "#00E5A0",
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      lineHeight: 1.7,
                      overflow: "auto",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      maxHeight: 220,
                    }}
                  >
                    {embedCode}
                  </Box>
                </Box>
              </SectionCard>

              {conditionCount > 0 && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: alpha("#A78BFA", 0.04),
                    border: `1px solid ${alpha("#A78BFA", 0.2)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "7px",
                        bgcolor: alpha("#A78BFA", 0.15),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <BoltOutlinedIcon
                        sx={{ fontSize: 14, color: "#A78BFA" }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: "#A78BFA", mb: 0.25, fontSize: 12 }}
                      >
                        {conditionCount} Conditional Rule
                        {conditionCount > 1 ? "s" : ""}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5, fontSize: 10 }}
                      >
                        Questions will dynamically show/hide based on user
                        answers.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── RIGHT PREVIEW PANEL ─── */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          borderRadius: "0 11px 11px 0",
        }}
      >
        {/* Soft neutral background */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderRadius: "0 11px 11px 0",
            bgcolor: theme.palette.mode === "dark" ? "#1A1F26" : "#F0F2F5",
          }}
        >
          {/* Subtle grid pattern */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: theme.palette.mode === "dark" ? 0.06 : 0.5,
              backgroundImage: `linear-gradient(${theme.palette.mode === "dark" ? "#334155" : "#E2E8F0"} 1px, transparent 1px), linear-gradient(90deg, ${theme.palette.mode === "dark" ? "#334155" : "#E2E8F0"} 1px, transparent 1px)`,
              backgroundSize: "40px 40px",
            }}
          />

          {/* Widget position — floating: bottom-right, side: right edge */}
          <Box
            sx={{
              position: "absolute",
              zIndex: 10,
              filter: `drop-shadow(0 12px 40px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.5 : 0.15)})`,
              ...(cfg.position === "floating"
                ? {
                    bottom: 32,
                    right: 32,
                  }
                : {
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }),
            }}
          >
            <WidgetPreview
              config={cfg}
              key={JSON.stringify(cfg.categories) + cfg.position + cfg.darkMode}
            />
          </Box>
        </Box>

        {/* Live Preview badge */}
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            p: "5px 12px",
            borderRadius: "6px",
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: "blur(8px)",
            border: `1px solid ${theme.palette.divider}`,
            zIndex: 200,
            fontSize: 11,
            fontWeight: 600,
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "#00E5A0",
              boxShadow: "0 0 6px #00E5A0",
            }}
          />
          Live Preview
        </Box>
      </Box>
    </Box>
  );
};

export default FeedbackWidgetConfigurator;
